import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import JinglePlayer from "@/components/JinglePlayer";
import { jingles } from "@/data/jingles";

export default function CustomJingles() {

  useEffect(() => {
    document.title =
      "Custom Jingles & Sonic Branding | 63 Original Tracks | Evolve Media Agency";
    const link =
      document.querySelector("link[rel='canonical']") ||
      document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute(
      "href",
      "https://evolvemediaagency.com/custom-jingles-sonic-branding"
    );
    if (!document.querySelector("link[rel='canonical']"))
      document.head.appendChild(link);

    const meta =
      document.querySelector("meta[name='description']") ||
      document.createElement("meta");
    meta.setAttribute("name", "description");
    meta.setAttribute(
      "content",
      "Listen to 63 original custom jingles produced for ecommerce and Amazon brands. Hear sonic branding built for Sponsored Brand video, Meta and TikTok ads, podcasts, and brand stores."
    );
    if (!document.querySelector("meta[name='description']"))
      document.head.appendChild(meta);

    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Custom Jingles & Sonic Branding",
      provider: {
        "@type": "Organization",
        name: "Evolve Media Agency",
        url: "https://evolvemediaagency.com",
      },
      areaServed: "Worldwide",
      description:
        "Original jingles, audio logos, and sonic branding for Amazon sellers and ecommerce brands. 63 productions in portfolio. Full commercial rights, WAV + MP3 + stems, multiple cut-downs.",
      url: "https://evolvemediaagency.com/custom-jingles-sonic-branding",
    });
    document.head.appendChild(jsonLd);
    window.scrollTo(0, 0);
    return () => {
      jsonLd.remove();
    };
  }, []);

  const filtered = jingles;


  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="bg-black py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-cta-green font-extrabold text-xs tracking-[2px] uppercase mb-4">
            SONIC BRANDING · 63 ORIGINAL TRACKS
          </p>
          <h1 className="text-section-dark-foreground font-black text-4xl md:text-6xl leading-tight mb-5">
            Custom Jingles Built to Get{" "}
            <span className="text-cta-green">Stuck</span> in a Customer's Head.
          </h1>
          <p className="text-section-dark-foreground/70 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-8">
            Original jingles, audio logos, and sonic branding for Amazon sellers
            and ecommerce brands. Hit play on any track below — every one was
            written, recorded, and mixed from scratch for a real brand.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href="https://calendly.com/evolvemedia/amz-30-min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-cta-green hover:bg-cta-green-hover text-white px-7 py-3 rounded-full text-base font-semibold transition"
            >
              Book a Sonic Branding Call
            </a>
            <a
              href="#portfolio"
              className="inline-block bg-cta-blue hover:bg-cta-blue-hover text-white px-7 py-3 rounded-full text-base font-semibold transition"
            >
              Listen to the Portfolio
            </a>
          </div>
        </div>
      </section>

      {/* Stat bar */}
      <section className="bg-section-light py-10">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { k: "63", v: "Original custom jingles in this portfolio" },
            { k: "10–14D", v: "Standard turnaround from brief to delivery" },
            { k: "100%", v: "Full commercial rights & ownership included" },
            { k: "6+", v: "Format cuts delivered per project (5s–45s)" },
          ].map((s) => (
            <div key={s.k}>
              <p className="text-3xl md:text-4xl font-black text-cta-blue mb-2">
                {s.k}
              </p>
              <p className="text-sm text-muted-foreground leading-snug">
                {s.v}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Why sonic branding */}
      <section className="bg-background py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
            Why a custom jingle out-converts stock music
          </h2>
          <p className="text-foreground/80 text-lg leading-relaxed mb-6">
            Stock music is the easiest way to make your brand feel forgettable.
            A custom jingle does one thing a beat library never can — it{" "}
            <strong>becomes the sound of your brand</strong>. When a customer
            hears the first two bars of your sonic signature over a Meta ad, on
            a podcast read, at the end of a Sponsored Brand video, or on a hold
            loop, they recognize you before they see your logo.
          </p>
          <p className="text-foreground/80 text-lg leading-relaxed">
            That kind of audio recall moves ad metrics, lowers CPA on
            repeat-exposure campaigns, and gives ecommerce brands a real edge
            over competitors still dropping royalty-free loops into every
            creative — especially for operators diversifying off Amazon and
            building real brand equity on Shopify.
          </p>
        </div>
      </section>

      {/* Portfolio */}
      <section id="portfolio" className="bg-section-light py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <p className="text-cta-green font-extrabold text-xs tracking-[2px] uppercase mb-3">
              THE PORTFOLIO
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              63 custom jingles. <em>Hit play.</em>
            </h2>
            <p className="text-muted-foreground text-base md:text-lg">
              Every card below is a live production. Filter by vocal, mood,
              industry, or genre to zero in on the sound closest to your brand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((j) => (
                <article
                  key={j.audioSlug}
                  className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow p-5 flex flex-col"
                >
                  <div className="mb-2">
                    <span className="inline-block text-[11px] uppercase tracking-wider font-bold text-cta-blue bg-cta-blue/10 px-2 py-1 rounded">
                      {j.industry}
                    </span>
                  </div>
                  <h3 className="font-black text-foreground text-lg leading-tight">
                    {j.brand}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    {j.product}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-1 text-[11px]">
                    <span className="bg-muted px-2 py-0.5 rounded text-foreground/70">
                      {j.genreLabel}
                    </span>
                    <span className="bg-muted px-2 py-0.5 rounded text-foreground/70 capitalize">
                      {j.mood}
                    </span>
                    <span className="bg-muted px-2 py-0.5 rounded text-foreground/70 capitalize">
                      {j.vocal} vocal
                    </span>
                  </div>
                  <JinglePlayer
                    src={`https://audio.evolveamz.com/${j.audioSlug}.mp3`}
                    title={`${j.brand} ${j.product}`}
                  />
                  <p className="text-sm text-foreground/70 mt-4 leading-relaxed">
                    {j.description}
                  </p>
                </article>
              ))}
            </div>
        </div>
      </section>

      {/* Where it runs */}
      <section className="bg-background py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-10 text-center">
            5 placements where a custom jingle pays back
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                t: "Amazon Sponsored Brand Video",
                d: "Outro audio logo + 5–10s sonic signature so shoppers remember your brand after the click.",
              },
              {
                t: "Meta & TikTok Ads",
                d: "30-second anthem cuts and 6-second hooks engineered to fight ad fatigue.",
              },
              {
                t: "Amazon Live & Brand Stores",
                d: "Background bed for live streams plus an audio logo to lead the brand store video.",
              },
              {
                t: "Podcast Reads & Sponsorships",
                d: "Pre-roll, mid-roll, and host-read intro/outro stings produced from one core melody.",
              },
              {
                t: "Hold Loops & On-Site Audio",
                d: "Reinforce your brand on every customer service touchpoint — not generic elevator music.",
              },
              {
                t: "Shopify & DTC Brand Sites",
                d: "Hero video soundtrack and email video stings for owned-channel marketing on Shopify.",
              },
            ].map((p) => (
              <div
                key={p.t}
                className="p-6 bg-section-light rounded-lg border-l-4 border-cta-green"
              >
                <h3 className="font-bold text-foreground mb-2">{p.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {p.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process / Deliverables */}
      <section className="bg-section-light py-16">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-5">
              The production process
            </h2>
            <ol className="space-y-4">
              {[
                ["Brief & moodboard", "Reference tracks, brand voice, where the jingle will run."],
                ["Demo round", "2–3 directional demos so you can pick a lane before we record vocals."],
                ["Recording & production", "Pro vocalists, full instrumentation, mix and master."],
                ["Cut-down delivery", "Multiple lengths (5s, 10s, 15s, 30s, 45s) plus stems and audio logo."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-4">
                  <span className="flex-shrink-0 w-9 h-9 bg-cta-blue text-white rounded-full flex items-center justify-center font-bold">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{t}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {d}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-5">
              What you receive
            </h2>
            <ul className="space-y-3">
              {[
                "WAV + MP3 masters in broadcast quality",
                "Full instrument & vocal stems",
                "5s, 10s, 15s, 30s, and 45s cuts",
                "Standalone audio logo",
                "Full commercial rights & ownership — no royalties, no licensing fees",
                "10–14 day standard turnaround",
              ].map((d) => (
                <li key={d} className="flex gap-3 items-start">
                  <span className="text-cta-green font-bold mt-0.5">✓</span>
                  <span className="text-foreground/80">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-5">
            {[
              {
                q: "Do I own the jingle outright?",
                a: "Yes. Full commercial rights and ownership are included. No royalties, no usage fees, no licensing windows. Use it wherever you want, forever.",
              },
              {
                q: "How long does it take?",
                a: "Standard turnaround is 10–14 days from approved brief to final deliverables — including 2–3 directional demos before vocals are tracked.",
              },
              {
                q: "Can you match a reference track?",
                a: "Yes. We work from your reference tracks and moodboard, then build something original that captures the same energy without copying.",
              },
              {
                q: "What if I need extra cut-downs later?",
                a: "We deliver stems with every project, so additional cuts and edits are quick and inexpensive. Just send the spec.",
              },
              {
                q: "Do you handle voice-over only briefs?",
                a: "Yes — audio logos and short-form sonic signatures are part of the same workflow.",
              },
            ].map((f) => (
              <div
                key={f.q}
                className="p-6 bg-section-light rounded-lg border-l-4 border-cta-blue"
              >
                <h3 className="font-bold text-foreground mb-2">{f.q}</h3>
                <p className="text-foreground/70 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="bg-section-light py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6 text-center">
            Pair sonic branding with the rest of your content stack
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link
              to="/video-portfolio"
              className="block p-5 bg-white rounded-lg shadow-sm hover:shadow-md transition border-l-4 border-cta-blue"
            >
              <h3 className="font-bold text-foreground mb-1">🎬 Video Portfolio</h3>
              <p className="text-sm text-muted-foreground">
                See the product videos these jingles soundtrack.
              </p>
            </Link>
            <Link
              to="/amazon-product-video-production"
              className="block p-5 bg-white rounded-lg shadow-sm hover:shadow-md transition border-l-4 border-cta-green"
            >
              <h3 className="font-bold text-foreground mb-1">📈 Video Production Guide</h3>
              <p className="text-sm text-muted-foreground">
                Why product video lifts conversion 9–15%.
              </p>
            </Link>
            <Link
              to="/ecommerce-content-roi"
              className="block p-5 bg-white rounded-lg shadow-sm hover:shadow-md transition border-l-4 border-cta-blue"
            >
              <h3 className="font-bold text-foreground mb-1">💰 Content ROI</h3>
              <p className="text-sm text-muted-foreground">
                The math on why pro content pays back fast.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA + outbound */}
      <section className="bg-black py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-section-dark-foreground mb-4">
            Ready to make your brand sound like itself?
          </h2>
          <p className="text-section-dark-foreground/70 text-lg mb-8">
            Book a 30-minute call and we'll talk through references, placements,
            and what a sonic identity could do for your CPA on cold traffic.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mb-6">
            <a
              href="https://calendly.com/evolvemedia/amz-30-min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-cta-green hover:bg-cta-green-hover text-white px-7 py-3 rounded-full font-semibold transition"
            >
              Book a Strategy Call
            </a>
            <a
              href="https://evolveamz.com/custom-jingles/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-cta-blue hover:bg-cta-blue-hover text-white px-7 py-3 rounded-full font-semibold transition"
            >
              Full Sonic Branding Service →
            </a>
          </div>
          <p className="text-section-dark-foreground/50 text-sm">
            Want the full agency-level breakdown? See the complete sonic
            branding service page on{" "}
            <a
              href="https://evolveamz.com/custom-jingles/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cta-green hover:underline"
            >
              EvolveAMZ.com
            </a>
            .
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
